import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

function FixAndFlipCalculator() {
  const [purchasePrice, setPurchasePrice] = useState(200000);
  const [repairCost, setRepairCost] = useState(50000);
  const [afterRepairValue, setAfterRepairValue] = useState(320000);
  const [holdingCost, setHoldingCost] = useState(15000);
  const [sellingCost, setSellingCost] = useState(20000);
  const [numYears, setNumYears] = useState(5);
  const [patternInput, setPatternInput] = useState("1,2,3,4,5");
  const [focusedInput, setFocusedInput] = useState(null);

  const payoffPattern = patternInput
    .split(",")
    .map((str) => parseInt(str.trim()))
    .filter((n) => !isNaN(n) && n >= 0);

  useEffect(() => {
    if (payoffPattern.length !== numYears) {
      setNumYears(payoffPattern.length);
    }
  }, [patternInput, numYears, payoffPattern.length]);

  const totalCost =
    Number(purchasePrice) +
    Number(repairCost) +
    Number(holdingCost) +
    Number(sellingCost);
  const profit = afterRepairValue - totalCost;

  const data = [
    { name: "Purchase", value: Number(purchasePrice) },
    { name: "Repairs", value: Number(repairCost) },
    { name: "Holding", value: Number(holdingCost) },
    { name: "Selling", value: Number(sellingCost) },
    { name: "Profit", value: profit },
  ];

  const patternSum = payoffPattern.reduce((a, b) => a + b, 0) || 1;
  const yearlyPayouts = payoffPattern.map((units) =>
    profit > 0 ? (units / patternSum) * profit : 0
  );

  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 550);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 550);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className="ffc-container">
      <h2 className="ffc-title">Mortgage Fix & Flip Calculator</h2>

      <div className="ffc-grid">
        {[
          {
            label: "Purchase Price ($)",
            value: purchasePrice,
            setValue: setPurchasePrice,
            name: "purchasePrice",
          },
          {
            label: "Repair Cost ($)",
            value: repairCost,
            setValue: setRepairCost,
            name: "repairCost",
          },
          {
            label: "After Repair Value ($)",
            value: afterRepairValue,
            setValue: setAfterRepairValue,
            name: "afterRepairValue",
          },
          {
            label: "Holding Cost ($)",
            value: holdingCost,
            setValue: setHoldingCost,
            name: "holdingCost",
          },
          {
            label: "Selling Cost ($)",
            value: sellingCost,
            setValue: setSellingCost,
            name: "sellingCost",
          },
        ].map(({ label, value, setValue, name }) => (
          <label key={name} className="ffc-label">
            {label}
            <input
              type="number"
              value={value}
              min={0}
              onChange={(e) => setValue(e.target.value)}
              onFocus={() => setFocusedInput(name)}
              onBlur={() => setFocusedInput(null)}
              className={`ffc-input ${
                focusedInput === name ? "ffc-input-focus" : ""
              }`}
            />
          </label>
        ))}
      </div>

      <div className="ffc-grid ffc-extra-spacing">
        <label className="ffc-label">
          Number of Years for Payoff Projection
          <input
            type="number"
            value={numYears}
            min={1}
            max={5}
            onChange={(e) => {
              let val = parseInt(e.target.value);
              if (isNaN(val) || val < 1) val = 1;
              else if (val > 5) val = 5;
              setNumYears(val);
              const currentPattern = payoffPattern.slice();
              while (currentPattern.length < val) currentPattern.push(val);
              while (currentPattern.length > val) currentPattern.pop();
              setPatternInput(currentPattern.join(","));
            }}
            onFocus={() => setFocusedInput("numYears")}
            onBlur={() => setFocusedInput(null)}
            className={`ffc-input ${
              focusedInput === "numYears" ? "ffc-input-focus" : ""
            }`}
          />
        </label>

        <label className="ffc-label">
          Payoff Pattern (comma-separated numbers, 1,2,3,4,5)
          <input
            type="text"
            value={patternInput}
            onChange={(e) => setPatternInput(e.target.value)}
            onFocus={() => setFocusedInput("patternInput")}
            onBlur={() => setFocusedInput(null)}
            className={`ffc-input monospace ${
              focusedInput === "patternInput" ? "ffc-input-focus" : ""
            }`}
          />
        </label>
        <div className="RACrightSideBottom00">
          <div>
            <span style={{ color: "red" }}>
              * PMI usually applies when your downpayment or equity in the home
              is under 20%.
            </span>
          </div>
        </div>
      </div>

      <div className={`ffc-profit ${profit >= 0 ? "green" : "red"}`}>
        Estimated Profit: {profit >= 0 ? "+" : "-"}$
        {Math.abs(profit).toLocaleString(undefined, {
          maximumFractionDigits: 2,
        })}
      </div>

      <ResponsiveContainer width="100%" height={300}>
        <BarChart
          data={data}
          margin={{ top: 20, right: 20, left: 25, bottom: 10 }}
        >
          <XAxis
            dataKey="name"
            stroke="#16a34a"
            tick={{ fontWeight: "600", fontSize: 15 }}
            angle={isMobileView ? -90 : 0}
            textAnchor={isMobileView ? "end" : "middle"}
            height={isMobileView ? 100 : 30}
          />
          <YAxis
            stroke="#16a34a"
            tickFormatter={(val) =>
              `$${val.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
            }
          />
          <Tooltip
            formatter={(value) =>
              `$${value.toLocaleString(undefined, {
                maximumFractionDigits: 2,
              })}`
            }
            cursor={{ fill: "rgba(22, 163, 74, 0.1)" }}
          />
          <CartesianGrid stroke="#e0e0e0" strokeDasharray="5 5" />
          <Bar
            dataKey="value"
            fill="#22c55e"
            radius={[8, 8, 0, 0]}
            barSize={175}
          />
        </BarChart>
      </ResponsiveContainer>

      <h3 className="ffc-subtitle">
        {numYears}-Year Profit Payout Projection (Pattern: {patternInput})
      </h3>

      <table className="ffc-table">
        <thead>
          <tr>
            {payoffPattern.map((_, i) => (
              <th key={i} className="ffc-th">
                Year {i + 1}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            {yearlyPayouts.map((yearProfit, i) => (
              <td
                key={i}
                className={`ffc-td ${yearProfit >= 0 ? "green" : "red"}`}
              >
                $
                {yearProfit.toLocaleString(undefined, {
                  maximumFractionDigits: 2,
                })}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default FixAndFlipCalculator;
